// 알림톡 API 연동 (카카오 알림톡)
// 실제 프로덕션에서는 백엔드에서 처리해야 함 (보안상 이유)

import { ExhibitionType } from '../types';

// 알림톡 발송 환경 설정
const NOTIFICATION_CONFIG = {
  senderId: process.env.REACT_APP_ALIMTALK_SENDER_ID || '',
  templateCode: process.env.REACT_APP_ALIMTALK_TEMPLATE_CODE || 'PRE_REGISTER_INFO',
  // 🚀 Railway Express 서버를 통한 실제 알림톡 발송
  environment: (process.env.REACT_APP_ENVIRONMENT || 'development').trim(),
  expressServerUrl: process.env.REACT_APP_EXPRESS_SERVER_URL || '',
  enableLogging: process.env.REACT_APP_ENABLE_LOGGING === 'true',
  apiTimeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000'),  
  domain: process.env.REACT_APP_DOMAIN || 'localhost'
};

// NHN Cloud 알림톡 API 설정
const NHN_ALIMTALK_CONFIG = {
  url: process.env.REACT_APP_NHN_ALIMTALK_URL || 'https://api-alimtalk.cloud.toast.com',
  appkey: process.env.REACT_APP_NHN_ALIMTALK_APPKEY || '',
  secretKey: process.env.REACT_APP_NHN_ALIMTALK_SECRET_KEY || '',
};

// 전시회별 정보
const EXHIBITION_INFO = {
  urbanbreak: {
    name: 'UrbanBreak',
    displayName: 'UrbanBreak2025',
    date: '2025년 8월 7일 (목) ~ 10일 (일)',
    time: '오전 10시 ~ 오후 6시',
    location: '서울 코엑스 Hall B1',
    contact: '02-1661-0118',
    email: 'info@urbanbreak.com',
    description: '2025년 제 6회를 맞이한 어반브레이크는 ‘PLAY WITH ARTIST’을 주제로 다양한 아티스트와 소통하고 교류할 수 있는 국내 최대 규모의 아트 페스티벌로 자리 잡고 있습니다.',
  },
  toycon: {
    name: 'ToyCon',
    displayName: 'TOY CON SEOUL 2025',
    date: '2025년 8월 7일 (목) ~ 10일 (일)',
    time: '오전 10시 ~ 오후 6시',
    location: '서울 코엑스 Hall B2',
    contact: '02-1661-0118',
    email: 'info@toyconseoul.com',
    description: '아트토이와 일상이 하나되는 순간, 토이콘 서울에 함께해주셔서 감사합니다. 전 세계 10개국 100여 명의 아티스트, 브랜드, 컬렉터들이 참여하는 국내 유일·최대 규모의 컬렉터블 토이 페어입니다. 준비되셨나요? TOYFUL한 순간들이 곧 펼쳐집니다! #TOYFUL LIFE #IP MARKET BE PLAYFUL! LIVE TOYFUL!',
  },
} as const;

// 알림톡 메시지 타입
export interface AlimtalkMessage {
  to: string; // 수신자 전화번호
  templateCode: string; // 템플릿 코드
  templateParameter: Record<string, string>; // 템플릿 변수
  exhibitionType: ExhibitionType; // 전시회 타입
  resendParameter?: {
    isResend: boolean;
    resendType: 'SMS' | 'LMS';
    resendTitle?: string;
    resendContent: string;
  };
}

// 알림톡 발송 응답 타입
export interface AlimtalkResponse {
  success: boolean;
  requestId: string;
  message: string;
  data?: any;
}

// 등록 완료 알림톡 데이터
export interface RegistrationNotificationData {
  name: string;
  phone: string;
  registrationId: string;
  registrationCode: string;
  exhibitionType: ExhibitionType;
  ticket_name?: string; // 티켓명 추가
  qrCodeUrl?: string;
}

/**
 * 알림톡 메시지 발송 (백엔드 API 호출)
 */
export async function sendAlimtalkMessage(message: AlimtalkMessage): Promise<AlimtalkResponse> {
  try {
    // 전시회별 알림톡 설정
    const exhibitionAlimtalkConfig = {
      urbanbreak: {
        templateCode: 'PRE_REGISTER_INFO',
        senderProfile: 'e75ae0b7fecb7d25b3db835ce28c7ae05f702392',
      },
      toycon: {
        templateCode: 'PRE_REGISTER_INFO_TO',
        senderProfile: '79a3c73c4ed976ebbe9c089ee42593d53a62c29c',
      },
    };
    
    const alimtalkConfig = exhibitionAlimtalkConfig[message.exhibitionType];
    
    console.log('🔔 알림톡 발송 요청:', {
      to: message.to,
      exhibitionType: message.exhibitionType,
      templateCode: alimtalkConfig.templateCode,
      senderProfile: alimtalkConfig.senderProfile,
      environment: NOTIFICATION_CONFIG.environment,
      enableLogging: NOTIFICATION_CONFIG.enableLogging,
    });

    console.log('🌍 환경 확인:', NOTIFICATION_CONFIG.environment);
    
    // CORS 문제와 보안을 위해 시뮬레이션 모드 사용 (프로덕션에서는 백엔드에서 호출)
    const useNHNCloudAPI = NOTIFICATION_CONFIG.environment === 'production'; // 프로덕션에서는 실제 API 호출
    
    if (useNHNCloudAPI) {
      console.log('🚀 Railway Express 서버를 통한 실제 알림톡 API 호출 시작');
      
      try {
        // Railway Express 서버 API 엔드포인트
        const apiEndpoint = `${NOTIFICATION_CONFIG.expressServerUrl}/api/alimtalk/send`;
        
        const requestData = {
          to: message.to,
          exhibitionType: message.exhibitionType,
          templateCode: alimtalkConfig.templateCode,
          senderProfile: alimtalkConfig.senderProfile,
          templateParameter: message.templateParameter,
        };

        console.log('📤 Railway Express API 요청:', {
          url: apiEndpoint,
          senderProfile: `${alimtalkConfig.senderProfile.slice(0, 8)}...`,
          templateCode: alimtalkConfig.templateCode,
          recipientNo: message.to
        });

        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        const result = await response.json();

        console.log('📥 Railway Express API 응답:', result);

        if (!response.ok || !result.success) {
          throw new Error(`Railway Express API 오류: ${result.message || response.statusText}`);
        }

        // 성공 응답
        return {
          success: true,
          requestId: result.requestId,
          message: result.message || '알림톡 발송 완료',
        } as AlimtalkResponse;

      } catch (error: any) {
        console.error('❌ Railway Express API 호출 실패:', error);
        
        // Railway Express 서버 연결 실패 시 시뮬레이션으로 폴백
        if (error.message?.includes('fetch') || error.message?.includes('NetworkError') || error.message?.includes('404')) {
          console.log('🔄 Railway Express 서버 연결 실패 - 시뮬레이션 모드로 전환');
          
          // 시뮬레이션 모드로 폴백
          return {
            success: true,
            requestId: `RAILWAY-FALLBACK-${Date.now()}`,
            message: '알림톡 발송 완료 (Railway Express 서버 연결 실패로 시뮬레이션)',
          } as AlimtalkResponse;
        }
        
        throw error;
      }
    }

    // 개발 환경에서는 시뮬레이션
    console.log('🧪 개발 환경: 시뮬레이션 실행');
    console.log('📱 알림톡 발송 시뮬레이션:', {
      수신자: message.to,
      전시회: message.exhibitionType,
      템플릿코드: alimtalkConfig.templateCode,
      발신프로필: alimtalkConfig.senderProfile,
      템플릿: message.templateParameter,
    });

    await new Promise(resolve => setTimeout(resolve, 1500));

    return {
      success: true,
      requestId: `DEV-${Date.now()}`,
      message: '알림톡 발송 완료 (시뮬레이션)',
    };
  } catch (error) {
    console.error('❌ 알림톡 발송 에러:', error);
    return {
      success: false,
      requestId: '',
      message: error instanceof Error ? error.message : '알림톡 발송 실패',
    };
  }
}

/**
 * 사전등록 완료 알림톡 발송
 */
export async function sendRegistrationCompleteNotification(
  data: RegistrationNotificationData
): Promise<AlimtalkResponse> {
  try {
    // 전화번호 포맷 정리 (010-1234-5678 -> 01012345678)
    const cleanPhone = data.phone.replace(/[^0-9]/g, '');
    
    // 전시회 정보 가져오기
    const exhibitionInfo = EXHIBITION_INFO[data.exhibitionType];
    
    // QR 코드 URL 생성
    const qrCodeUrl = data.qrCodeUrl || `${NOTIFICATION_CONFIG.domain}/complete/${data.registrationCode}`;
    
    // 등록일 포맷 (YYYY-MM-DD)
    const registerDate = new Date().toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\./g, '-').replace(/\s/g, '').slice(0, -1);
    
    // 알림톡 템플릿 파라미터 구성 (NHN Cloud API: 14자 제한, 공백 제거)
    const templateParameter = {
      name: String(data.name).trim().substring(0, 14),
      ticket_name: String(data.ticket_name || '일반 입장권').trim().substring(0, 14),
      register_code: String(data.registrationCode).trim().substring(0, 14),
      register_date: String(registerDate).trim().substring(0, 14),
      exhibition_name: exhibitionInfo.displayName,
    };

    // 템플릿 유효성 검증
    if (!validateAlimtalkTemplate(templateParameter)) {
      return {
        success: false,
        requestId: '',
        message: '알림톡 템플릿 유효성 검증 실패',
      };
    }

    // SMS 대체 발송 메시지 (새로운 템플릿 형식으로)
    const resendContent = `🎫 ${exhibitionInfo.displayName} 🎫 사전등록자 안내

${data.name}님, 2025년 제 6회를 맞이한 ${exhibitionInfo.name}는 'PLAY WITH ARTIST'을 주제로 다양한 아티스트와 소통하고 교류할 수 있는 국내 최대 규모의 아트 페스티벌로 자리 잡고 있습니다.

■ ${data.ticket_name || '일반 입장권'}
■ 사전등록ID : ${data.registrationCode}
■ 등록일 : ${registerDate}
■ 전시회명 : ${exhibitionInfo.displayName}

📋 전시회 정보
• 날짜: ${exhibitionInfo.date}
• 시간: ${exhibitionInfo.time}
• 장소: ${exhibitionInfo.location}

🎫 입장안내
• QR 코드를 저장하여 입장 시 제시해주세요
• 신분증을 함께 지참해주세요
• QR 코드 확인: ${qrCodeUrl}

📞 문의
• 전화: ${exhibitionInfo.contact}
• 이메일: ${exhibitionInfo.email}

감사합니다!`;

    // 알림톡 메시지 구성
    const message: AlimtalkMessage = {
      to: cleanPhone,
      templateCode: NOTIFICATION_CONFIG.templateCode,
      exhibitionType: data.exhibitionType,
      templateParameter,
      resendParameter: {
        isResend: true,
        resendType: 'LMS',
        resendTitle: `🎫 ${exhibitionInfo.displayName} 🎫 사전등록 완료`,
        resendContent,
      },
    };

    const response = await sendAlimtalkMessage(message);
    
    if (NOTIFICATION_CONFIG.enableLogging) {
      console.log('📨 사전등록 알림톡 발송 결과:', response);
    }

    return response;
  } catch (error) {
    console.error('❌ 사전등록 알림톡 발송 에러:', error);
    return {
      success: false,
      requestId: '',
      message: error instanceof Error ? error.message : '알림톡 발송 실패',
    };
  }
}

/**
 * 입장 완료 알림톡 발송
 */
export async function sendEntryCompleteNotification(
  data: RegistrationNotificationData
): Promise<AlimtalkResponse> {
  try {
    const cleanPhone = data.phone.replace(/[^0-9]/g, '');
    const exhibitionInfo = EXHIBITION_INFO[data.exhibitionType];
    const entryTime = new Date().toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const templateParameter = {
      name: data.name,
      exhibition_name: exhibitionInfo.displayName,
      registration_id: data.registrationCode,
      entry_time: entryTime,
      exhibition_location: exhibitionInfo.location,
      contact_phone: exhibitionInfo.contact,
    };

    const resendContent = `[${exhibitionInfo.displayName} 입장 완료]

안녕하세요 ${data.name}님,
${exhibitionInfo.displayName} 입장이 완료되었습니다.

📋 입장정보
• 등록번호: ${data.registrationCode}
• 입장시간: ${entryTime}
• 장소: ${exhibitionInfo.location}

${exhibitionInfo.description}를 즐겁게 관람해주세요!

📞 문의: ${exhibitionInfo.contact}`;

    const message: AlimtalkMessage = {
      to: cleanPhone,
      templateCode: 'ENTRY_COMPLETE', // 입장 완료용 템플릿
      exhibitionType: data.exhibitionType,
      templateParameter,
      resendParameter: {
        isResend: true,
        resendType: 'SMS',
        resendTitle: `[${exhibitionInfo.displayName}] 입장 완료`,
        resendContent,
      },
    };

    const response = await sendAlimtalkMessage(message);
    
    if (NOTIFICATION_CONFIG.enableLogging) {
      console.log('🎫 입장 완료 알림톡 발송 결과:', response);
    }

    return response;
  } catch (error) {
    console.error('❌ 입장 완료 알림톡 발송 에러:', error);
    return {
      success: false,
      requestId: '',
      message: error instanceof Error ? error.message : '알림톡 발송 실패',
    };
  }
}

/**
 * 알림톡 템플릿 유효성 검증
 */
export function validateAlimtalkTemplate(templateParameter: Record<string, string>): boolean {
  const requiredFields = ['name', 'ticket_name', 'register_code', 'register_date', 'exhibition_name'];
  
  for (const field of requiredFields) {
    if (!templateParameter[field] || templateParameter[field].trim() === '') {
      console.error(`❌ 필수 템플릿 필드 누락: ${field}`);
      return false;
    }
  }
  
  return true;
}

/**
 * 전화번호 유효성 검증
 */
export function validatePhoneNumber(phone: string): boolean {
  const cleanPhone = phone.replace(/[^0-9]/g, '');
  return /^010\d{8}$/.test(cleanPhone);
}

/**
 * 전시회 정보 가져오기
 */
export function getExhibitionInfo(exhibitionType: ExhibitionType) {
  return EXHIBITION_INFO[exhibitionType];
}

/**
 * 알림톡 설정 정보 가져오기
 */
export function getNotificationConfig() {
  return {
    ...NOTIFICATION_CONFIG,
    senderId: NOTIFICATION_CONFIG.senderId ? '설정됨' : '미설정',
  };
} 